// Canonical public integration catalogue.
//
// Keep this module free of JSX and browser globals. The Integrations page,
// regional logo clusters, and illustrative connected-tools preview all project
// from this source so a logo, alias, or preview badge cannot create a second
// availability claim.

import {
  IMPORT_CAPABILITY_IDS,
  LIMITED_CAPABILITIES,
} from './integration-capabilities.js';

export { IMPORT_CAPABILITY_IDS } from './integration-capabilities.js';

export const INTEGRATION_EVIDENCE_FLAGS = Object.freeze({
  // An illustrative dashboard row. It is not a provider availability or a
  // customer connection claim, so it does not independently require evidence.
  example_connected: Object.freeze({ capabilityClaim: false, requiresEvidence: false }),
  // A connector that ships in the product, reviewed in application source.
  limited_setup: Object.freeze({ capabilityClaim: true, requiresEvidence: true }),
});

const PREVIEW_EVIDENCE_BY_ID = Object.freeze({
  jira: 'example_connected',
  github: 'example_connected',
  'google-calendar': 'example_connected',
  cursor: 'example_connected',
  'github-copilot': 'example_connected',
  gitlab: 'limited_setup',
});

// Visual presentation fields for every vendor recognised on the site.
// Optional `file` and `fileDark` identify committed logo assets. Every item
// is classified below as an available connector or roadmap only.
const PRESENTATION = [
  { name: 'Jira', slug: 'jira', category: 'work-tracking', regions: ['north-america', 'brazil', 'europe'], global: true, color: '#2684FF' },
  { name: 'GitHub', slug: 'github', category: 'source-control', regions: ['north-america', 'brazil', 'europe'], global: true, color: '#181717' },
  { name: 'GitLab', slug: 'gitlab', category: 'source-control', regions: ['north-america', 'brazil', 'europe'], global: true, color: '#FC6D26' },
  { name: 'Bitbucket', slug: 'bitbucket', category: 'source-control', regions: ['north-america', 'brazil', 'europe'], global: true, color: '#0052CC' },
  { name: 'Azure DevOps', slug: 'azure-devops', category: 'work-tracking', regions: ['north-america', 'brazil', 'europe'], global: true, color: '#0078D4' },
  { name: 'Google Calendar', slug: 'google-calendar', category: 'collaboration', regions: ['north-america', 'brazil', 'europe'], global: true, color: '#4285F4' },
  { name: 'Microsoft 365 Calendar', slug: 'microsoft-365', category: 'collaboration', regions: ['north-america', 'brazil', 'europe'], global: true, color: '#D83B01' },
  { name: 'GitHub Copilot', slug: 'github-copilot', file: 'microsoft-copilot.svg', category: 'ai-delivery', regions: ['north-america', 'brazil', 'europe'], global: true, color: '#2253CE' },
  { name: 'Cursor', slug: 'cursor', fileDark: 'cursor-light.svg', category: 'ai-delivery', regions: ['north-america', 'brazil', 'europe'], global: true, color: '#1A1A1A' },
  { name: 'Claude Code', slug: 'claude-code', file: 'claude-ai.svg', category: 'ai-delivery', regions: ['north-america', 'brazil', 'europe'], global: true, color: '#D97757' },
  { name: 'Tempo', slug: 'tempo', category: 'time-tracking', regions: ['north-america', 'europe'], global: true, color: '#1F73B7' },
  { name: 'Harvest', slug: 'harvest', category: 'time-tracking', regions: ['north-america'], global: true, color: '#FB7507' },
  { name: 'Toggl Track', slug: 'toggl', file: 'toggl-track.svg', category: 'time-tracking', regions: ['north-america', 'brazil', 'europe'], global: true, color: '#E57CD8' },
  { name: 'Clockify', slug: 'clockify', category: 'time-tracking', regions: ['north-america', 'brazil', 'europe'], global: true, color: '#03A9F4' },
  { name: 'Productive', slug: 'productive', category: 'agency-ops', regions: ['north-america', 'europe'], global: true, color: '#5236FF' },
  { name: 'QuickBooks', slug: 'quickbooks', category: 'finance-accounting', regions: ['north-america'], global: true, color: '#2CA01C' },
  { name: 'Xero', slug: 'xero', category: 'finance-accounting', regions: ['europe'], global: true, color: '#13B5EA' },
  { name: 'Exact Online', slug: 'exact-online', category: 'finance-accounting', regions: ['europe'], global: true, color: '#ED1C24' },
  { name: 'Conta Azul', slug: 'conta-azul', category: 'finance-accounting', regions: ['brazil'], global: true, color: '#0064FF' },
  { name: 'Omie', slug: 'omie', category: 'finance-accounting', regions: ['brazil'], global: true, color: '#00B956' },
  { name: 'HubSpot', slug: 'hubspot', category: 'crm', regions: ['north-america', 'brazil', 'europe'], global: true, color: '#FF7A59' },
  { name: 'Salesforce', slug: 'salesforce', category: 'crm', regions: ['north-america', 'europe'], global: true, color: '#00A1E0' },
  { name: 'Pipedrive', slug: 'pipedrive', category: 'crm', regions: ['north-america', 'brazil', 'europe'], global: true, color: '#1A1A1A' },
  { name: 'RD Station', slug: 'rd-station', category: 'crm', regions: ['brazil'], global: true, color: '#1FB25A' },
  { name: 'AWS', slug: 'aws', fileDark: 'aws-light.svg', category: 'cloud-cost', regions: ['north-america', 'brazil', 'europe'], global: true, color: '#FF9900' },
  { name: 'Microsoft Azure', slug: 'azure', category: 'cloud-cost', regions: ['north-america', 'brazil', 'europe'], global: true, color: '#0078D4' },
  { name: 'Google Cloud', slug: 'google-cloud', category: 'cloud-cost', regions: ['north-america', 'brazil', 'europe'], global: true, color: '#4285F4' },
  { name: 'Datadog', slug: 'datadog', category: 'observability', regions: ['north-america', 'brazil', 'europe'], global: true, color: '#632CA6' },
  { name: 'Sentry', slug: 'sentry', category: 'observability', regions: ['north-america', 'brazil', 'europe'], global: true, color: '#362D59' },
  { name: 'New Relic', slug: 'new-relic', category: 'observability', regions: ['north-america', 'brazil', 'europe'], global: true, color: '#1CE783' },
  { name: 'Grafana Cloud', slug: 'grafana', file: 'grafana-cloud.svg', category: 'observability', regions: ['north-america', 'brazil', 'europe'], global: true, color: '#F46800' },
  { name: 'Slack', slug: 'slack', category: 'collaboration', regions: ['north-america', 'brazil', 'europe'], global: true, color: '#4A154B' },
  { name: 'Microsoft Teams', slug: 'teams', file: 'microsoft-teams.svg', category: 'collaboration', regions: ['north-america', 'brazil', 'europe'], global: true, color: '#4B53BC' },
  { name: 'Linear', slug: 'linear', category: 'work-tracking', regions: ['north-america', 'brazil', 'europe'], global: true, color: '#5E6AD2' },
  { name: 'monday.com', slug: 'monday', category: 'work-tracking', regions: ['north-america', 'brazil', 'europe'], global: true, color: '#FF3D57' },
  { name: 'ClickUp', slug: 'clickup', category: 'work-tracking', regions: ['north-america', 'brazil', 'europe'], global: true, color: '#7B68EE' },
  { name: 'Asana', slug: 'asana', category: 'work-tracking', regions: ['north-america', 'brazil', 'europe'], global: true, color: '#F06A6A' },
  { name: 'Trello', slug: 'trello', category: 'work-tracking', regions: ['north-america', 'brazil', 'europe'], global: true, color: '#0079BF' },
  { name: 'Teamleader', slug: 'teamleader', category: 'psa-resource-planning', regions: ['europe'], global: false, color: '#FF5440' },
  { name: 'Scoro', slug: 'scoro', category: 'psa-resource-planning', regions: ['europe'], global: false, color: '#0061FF' },
  { name: 'Accelo', slug: 'accelo', category: 'psa-resource-planning', regions: ['north-america'], global: false, color: '#3372FF' },
  { name: 'BigTime', slug: 'bigtime', category: 'psa-resource-planning', regions: ['north-america'], global: false, color: '#005EB8' },
  { name: 'Kantata', slug: 'kantata', category: 'psa-resource-planning', regions: ['north-america'], global: false, color: '#F26B21' },
  { name: 'Ruddr', slug: 'ruddr', category: 'psa-resource-planning', regions: ['north-america', 'europe'], global: false, color: '#2563EB' },
  { name: 'Forecast', slug: 'forecast', category: 'psa-resource-planning', regions: ['north-america', 'europe'], global: false, color: '#6358DC' },
  { name: 'Runrun.it', slug: 'runrunit', category: 'psa-resource-planning', regions: ['brazil'], global: false, color: '#FF6F00' },
  { name: 'Artia', slug: 'artia', category: 'psa-resource-planning', regions: ['brazil'], global: false, color: '#5E2B97' },
  { name: 'Sage', slug: 'sage', category: 'finance-accounting', regions: ['europe'], global: false, color: '#00DC06' },
];

export const INTEGRATION_CATALOG = Object.freeze(PRESENTATION.map((entry) => {
  const capability = LIMITED_CAPABILITIES[entry.name];
  const availability = capability ? 'limited_setup' : 'roadmap';
  return Object.freeze({
    ...entry,
    id: capability?.id || `roadmap-${entry.slug}`,
    aliases: capability?.aliases || [],
    availability,
    mode: capability?.mode || null,
    evidence: capability?.evidence || [],
    previewEvidence: PREVIEW_EVIDENCE_BY_ID[capability?.id] || 'limited_setup',
  });
}));

export const LIMITED_SETUP_INTEGRATIONS = Object.freeze(
  INTEGRATION_CATALOG.filter((integration) => integration.availability === 'limited_setup'),
);
export const ROADMAP_INTEGRATIONS = Object.freeze(
  INTEGRATION_CATALOG.filter((integration) => integration.availability === 'roadmap'),
);
export const LIMITED_SETUP_NAMES = Object.freeze(LIMITED_SETUP_INTEGRATIONS.map((integration) => integration.name));
export const ROADMAP_NAMES = Object.freeze(ROADMAP_INTEGRATIONS.map((integration) => integration.name));

// Roadmap tools grouped for the Integrations page. Each group is a projection
// of catalogue categories, so a roadmap tool lands in exactly one group and a
// tool with a shipped connector never appears here.
const ROADMAP_GROUP_CATEGORIES = [
  ['time-agency', ['time-tracking', 'agency-ops']],
  ['finance-crm', ['finance-accounting', 'crm']],
  ['quality-observability', ['observability']],
  ['work-management', ['work-tracking', 'collaboration']],
  ['psa-resource-planning', ['psa-resource-planning']],
];
export const ROADMAP_GROUPS = Object.freeze(ROADMAP_GROUP_CATEGORIES.map(([id, categories]) => Object.freeze({
  id,
  names: Object.freeze(ROADMAP_INTEGRATIONS
    .filter((integration) => categories.includes(integration.category))
    .map((integration) => integration.name)),
})));

export const REGIONAL_LOGO_CLUSTERS = Object.freeze(Object.fromEntries(
  ['north-america', 'brazil', 'europe'].map((region) => [
    region,
    LIMITED_SETUP_INTEGRATIONS
      .filter((integration) => integration.regions.includes(region))
      .map((integration) => integration.name),
  ]),
));

const CATALOG_BY_NAME_OR_ALIAS = new Map();
for (const integration of INTEGRATION_CATALOG) {
  CATALOG_BY_NAME_OR_ALIAS.set(integration.name, integration);
  for (const alias of integration.aliases) CATALOG_BY_NAME_OR_ALIAS.set(alias, integration);
}

export function getCatalogIntegration(name) {
  return CATALOG_BY_NAME_OR_ALIAS.get(name) || null;
}
